// src/pages/GradingHistoryPage.jsx
import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody,
  Chip, LinearProgress, TextField, MenuItem, Select,
  FormControl, InputLabel,
} from '@mui/material';
import GradeIcon from '@mui/icons-material/Grade';
import api from '../api/axios';

export default function GradingHistoryPage() {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [filter,  setFilter]  = useState('all');

  useEffect(() => {
    api.get('/me/stats?days=90')
      .then(r => setData(r.data?.grading_results || []))
      .catch(() => setError('Impossible de charger les resultats.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? data
    : data.filter(r => filter === 'passed' ? r.passed : !r.passed);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 3, py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <GradeIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h5" fontWeight={700}>Historique des examens</Typography>
      </Box>

      {/* Stats globales */}
      {!loading && !error && data.length > 0 && (() => {
        const passed  = data.filter(r => r.passed).length;
        const avg     = data.reduce((s, r) => s + (r.score || 0), 0) / data.length;
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 2, mb: 3 }}>
            {[
              { label: 'Examens passes', value: `${passed}/${data.length}`, color: '#0f766e' },
              { label: 'Taux de reussite', value: `${Math.round((passed/data.length)*100)}%`, color: '#4f46e5' },
              { label: 'Score moyen', value: `${avg.toFixed(1)}%`, color: '#b45309' },
            ].map(s => (
              <Paper key={s.label} variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                <Typography fontSize={13} color="text.secondary">{s.label}</Typography>
                <Typography fontSize={24} fontWeight={700} color={s.color}>{s.value}</Typography>
              </Paper>
            ))}
          </Box>
        );
      })()}

      {/* Filtre */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Filtrer</InputLabel>
          <Select value={filter} onChange={e => setFilter(e.target.value)} label="Filtrer">
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="passed">Reussis</MenuItem>
            <MenuItem value="failed">Echoues</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {error   && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell>Lab / Examen</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Resultat</TableCell>
                <TableCell>Declencheur</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Aucun resultat d examen.
                  </TableCell>
                </TableRow>
              ) : filtered.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontWeight: 500 }}>Lab #{r.lab_id || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate" value={Math.min(r.score || 0, 100)}
                        sx={{
                          width: 80, borderRadius: 1, height: 6,
                          bgcolor: r.passed ? '#ccfbf1' : '#fecaca',
                          '& .MuiLinearProgress-bar': { bgcolor: r.passed ? '#0f766e' : '#dc2626' },
                        }}
                      />
                      <Typography fontSize={13} fontWeight={600}>
                        {(r.score || 0).toFixed(1)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={r.passed ? 'Reussi' : 'Echoue'}
                      color={r.passed ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{r.trigger || '-'}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {r.graded_at ? new Date(r.graded_at).toLocaleString('fr') : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
